import {
    ConsolidatedLocationView,
    EventPanelFormType,
    EventPersonDto,
    EventVehicleDto,
} from '@mark43/cad-api';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebounce } from '@cad-arc';
import { useSelectedLocation } from '../hooks/use-selected-location';
import { RelatedDataStoreProvider } from '../../../related-data/related-data-provider';
import { RelatedDataPollNewEvent } from '../../../related-data/related-data-poll';
import { RelatedDataTabContentNewEvent } from './related-data-tab-content-new-event';
import { getRelatedDataQueriesForEntityPanel } from '../../../panels/entity-panels/related-data/related-data-queries';
import { useMemo } from 'react';
import { toJS } from 'mobx';

/**
 * If this is a new event panel, we should fetch data
 * as the form updates
 */
export function RelatedDataTabNewEvent() {
    const eventForm = useFormContext<EventPanelFormType>();

    /**
     * We debounce our watched values because
     * we don't want to refetch on every keystroke
     */
    const [reportingParties] = useDebounce(
        useWatch({
            name: 'reportingParties',
            control: eventForm.control,
        }),
        400
    );

    /**
     * We debounce our watched values because
     * we don't want to refetch on every keystroke
     */
    const [involvedPersons] = useDebounce(
        useWatch({
            name: 'involvedPersons',
            control: eventForm.control,
        }),
        400
    );

    /**
     * We debounce our watched values because
     * we don't want to refetch on every keystroke
     */
    const [vehicles] = useDebounce(
        useWatch({
            name: 'vehicles',
            control: eventForm.control,
        }),
        400
    );

    const location = useSelectedLocation();

    const personsValidated = useMemo(() => {
        const validReportingParties = reportingParties.filter(
            (reportingParty) =>
                !!reportingParty?.firstName ||
                !!reportingParty?.lastName ||
                !!reportingParty?.phoneNumber
        );
        const validInvolvedPersons = involvedPersons.filter(
            (involvedPerson) =>
                !!involvedPerson?.firstName ||
                !!involvedPerson?.lastName ||
                !!involvedPerson?.phoneNumber
        );

        return validReportingParties.concat(validInvolvedPersons);
    }, [reportingParties, involvedPersons]);

    const vehiclesValidated = useMemo(() => {
        const validVehicles = vehicles.filter(
            (vehicle) => !!vehicle?.plateNumber || !!vehicle?.vinNumber
        );

        return validVehicles;
    }, [vehicles]);

    const {
        vehicleQueries,
        personQueries,
        phoneNumbers,
        relatedDataLocationQuery,
    } = getRelatedDataQueriesForEntityPanel(
        personsValidated,
        vehiclesValidated,
        location,
        eventForm.getValues('locationSubPremise')
    );

    const rawMultiAgencyClassificationDtos = toJS(location?.multiAgencyClassificationDtos ?? []);

    const locationNormalized = {
        ...location,
        multiAgencyClassificationDtos: rawMultiAgencyClassificationDtos,
    };

    return (
        <RelatedDataStoreProvider>
            <RelatedDataPollNewEvent
                phoneNumbers={phoneNumbers}
                vehicleQueries={vehicleQueries}
                relatedDataLocationQuery={relatedDataLocationQuery}
                personQueries={personQueries}
            />
            <RelatedDataTabContentNewEvent
                vehicles={vehiclesValidated as EventVehicleDto[]}
                reportingParties={personsValidated as EventPersonDto[]}
                phoneNumbers={phoneNumbers}
                vehicleQueries={vehicleQueries}
                location={locationNormalized as ConsolidatedLocationView}
                relatedDataLocationQuery={relatedDataLocationQuery}
                personQueries={personQueries}
            />
        </RelatedDataStoreProvider>
    );
}
